// ThreadV5LineageExplorer — FXC-8618 (Thread-UI slice), extended for FXC-8628.
//
// Mounts @flexcompute/thread-graph's <LineageExplorer> inline inside the
// V5 versioned views with collapse-by-default semantics:
//
//   • initialDepth=1 → only 1-hop neighbourhood around the chosen pivot
//   • revealDepth=1  → each click reveals one more hop
//   • clusterBy="units" → clusters visible nodes by atomic-unit (source
//     tool / flight condition), giving clear expand/collapse affordances
//   • collapse=false by default; a toolbar toggle flips it so whole
//     clusters collapse into super-nodes for a structural overview
//
// FXC-8628 additions:
//   • fullSurface prop — when true the component renders in a full-page
//     layout (no fixed height, wider search box, visible selection panel).
//   • FXC-8632 Semantic search box: wired to /api/search via the existing
//     semanticSearch() client; results are passed as searchHits (Set<string>)
//     to <LineageExplorer> which reveals + highlights them.  Falls back to
//     label substring match when the endpoint returns nothing.
//   • FXC-8632 Selection detail panel: onSelect (LineageSelection) is used
//     to populate a V5 side panel showing node type/label/key metadata plus
//     direct 1-hop antecedents and descendants.
//   • FXC-8631 V5 token theming: chrome (toolbar, search box, selection panel,
//     cluster selector) uses --v5-* design tokens throughout.  Graph canvas
//     colours are NOT themed here — see FXC-8629 note below.
//
// FXC-8629 NOTE — graph canvas colour theming:
//   <LineageExplorer> does not yet expose colourForType / colourForPredicate /
//   nodeColor pass-through props (those live in <ThreadGraph> but are not
//   forwarded from <LineageExplorer> as of FXC-8618 baseline).  Warp ticket
//   FXC-8629 (warp/FXC-8629-lineage-colour-overrides) will add them.  Once
//   that lands, wire v5Colour here:
//     const v5Colour = (type: string) => getComputedStyle(document.documentElement)
//       .getPropertyValue(V5_TOKEN_FOR[type] ?? "--v5-primary").trim();
//     <LineageExplorer colourForType={v5Colour} ... />
//
// DATA-SHAPE DEPENDENCY (Shuttle):
//   The component calls /api/nodes/dag?project_id=…&branch=… which
//   **returns the entire project graph in one response**.  At X-57 scale
//   (76 608 nodes, 79 452 edges) this is a ~8-15 MB JSON payload that
//   is expensive to fetch and held in memory.  Only a tiny
//   neighbourhood is rendered at any moment (LineageExplorer never
//   passes the full graph to the canvas), but the fetch still serialises
//   the whole graph.
//
//   What Shuttle should provide for true lazy scale:
//     GET /api/v2/graph/neighbourhood?project_id=…&branch=…&focus=…&depth=1
//     → { nodes: [...], edges: [...] }   (just the requested neighbourhood)
//   Then this component can call that endpoint on every expand instead
//   of holding the giant graph client-side.  Until that endpoint exists
//   the component falls back to the full /nodes/dag fetch and degrades
//   gracefully: if the payload is too large the graph is capped at
//   DAG_NODE_RENDER_CAP nodes (picked randomly to keep something
//   visible rather than a blank panel).
//
// WARP COMPONENT SUGGESTION:
//   LineageExplorer should accept an optional `onRevealRequest` prop:
//     onRevealRequest?: (nodeIds: string[], depth: number) => Promise<Graph>
//   When provided, it delegates neighbourhood fetching to the host rather
//   than computing it from the backing graph.  This would allow a lazy-
//   loading mount with zero client-side graph state.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchLineage } from "../../api/artifacts";
import { semanticSearch } from "../../api/search";
import { fromProvenanceGraph } from "@flexcompute/thread-graph";
import type { Graph } from "@flexcompute/thread-graph";
import { LineageExplorer } from "@flexcompute/thread-graph/react";
import type { ClusterInfo, ClusterMode, GraphControls, LineageSelection } from "@flexcompute/thread-graph/react";
import { Icons } from "../thread-v4/icons";

// Hard cap on nodes passed to the backing graph when the endpoint
// returns more than this number.  The neighbourhood render is still
// O(visible-nodes), but the index build is O(N) so we cap early.
const DAG_NODE_RENDER_CAP = 8000;

// How long to debounce the semantic-search input (ms).
const SEARCH_DEBOUNCE_MS = 350;

// Default pivot type priority: we pick the highest-priority type that
// exists in the graph as the initial focus node.
const PIVOT_TYPE_PRIORITY = [
  "geometry",
  "source_dataset",
  "mesh",
  "surface_mesh",
  "volume_mesh",
  "solver_run",
  "run",
];

function pickPivotId(graph: Graph, selectedNodeId: string | null): string | null {
  // If a node is explicitly selected and exists in the graph, use it.
  if (selectedNodeId && graph.nodes.some((n) => n.id === selectedNodeId)) {
    return selectedNodeId;
  }
  // Pick the highest-priority type available.
  for (const type of PIVOT_TYPE_PRIORITY) {
    const found = graph.nodes.find((n) => n.type === type);
    if (found) return found.id;
  }
  // Fall back to the node with the highest out-degree (likely a root).
  if (graph.nodes.length === 0) return null;
  const degree = new Map<string, number>();
  for (const edge of graph.edges) {
    degree.set(edge.source, (degree.get(edge.source) ?? 0) + 1);
  }
  let best = graph.nodes[0]!;
  for (const node of graph.nodes) {
    if ((degree.get(node.id) ?? 0) > (degree.get(best.id) ?? 0)) best = node;
  }
  return best.id;
}

function capGraph(graph: Graph, cap: number): { graph: Graph; capped: boolean } {
  if (graph.nodes.length <= cap) return { graph, capped: false };
  const kept = new Set(graph.nodes.slice(0, cap).map((n) => n.id));
  return {
    graph: {
      ...graph,
      nodes: graph.nodes.slice(0, cap),
      edges: graph.edges.filter((e) => kept.has(e.source) && kept.has(e.target)),
    },
    capped: true,
  };
}

// FXC-8632: local label/substring fallback when semantic search returns nothing.
function labelSearchFallback(graph: Graph, query: string): Set<string> {
  const q = query.trim().toLowerCase();
  if (!q) return new Set();
  return new Set(
    graph.nodes
      .filter((n) => n.label.toLowerCase().includes(q) || n.id.toLowerCase().includes(q) || n.type.toLowerCase().includes(q))
      .map((n) => n.id),
  );
}

export interface ThreadV5LineageExplorerProps {
  projectId: string;
  branch: string;
  /** Currently selected run/node — used as the initial pivot when present. */
  selectedNodeId?: string | null;
  /** Called when the user selects a node; host can update URL state. */
  onNodeSelect?: (nodeId: string) => void;
  /** Called when the user wants to navigate to the full provenance surface. */
  onFullProvenance?: () => void;
  /**
   * FXC-8630: When true, renders in full-surface layout (expands to fill
   * the page, shows the search box prominently, shows the selection detail
   * panel as a side panel rather than a one-line chip).
   */
  fullSurface?: boolean;
  /** Panel dimensions — defaults to filling the parent. */
  width?: number;
  height?: number;
}

// FXC-8632: Selection detail side panel.
// Renders node type/label/key metadata + 1-hop antecedents/descendants.
// antecedents and descendants are GraphNode[] (direct 1-hop from LineageSelection).
function SelectionPanel({ selection, onDismiss }: {
  selection: LineageSelection;
  onDismiss: () => void;
}) {
  const { node, antecedents, descendants } = selection;

  // Collect readable metadata from node.data (strip internal _ keys + large values).
  const metaEntries = Object.entries(node.data ?? {})
    .filter(([k, v]) => !k.startsWith("_") && v !== null && v !== undefined && typeof v !== "object")
    .slice(0, 8);

  return (
    <aside className="v5-lineage-selection-panel" data-testid="v5-lineage-selection-panel" aria-label="Selected node details">
      <header className="v5-lineage-selection-header">
        <div className="v5-lineage-selection-title">
          <span className="v5-chip v5-chip--type">{node.type}</span>
          <span className="v5-lineage-selection-label">{node.label}</span>
        </div>
        <button
          className="v5-icon-button"
          type="button"
          title="Dismiss"
          aria-label="Dismiss selection panel"
          onClick={onDismiss}
        >
          <Icons.ChevronRight size={13} />
        </button>
      </header>

      {metaEntries.length > 0 && (
        <section className="v5-lineage-selection-section" aria-label="Metadata">
          <span className="v5-eyebrow">Metadata</span>
          <dl className="v5-lineage-selection-meta">
            {metaEntries.map(([k, v]) => (
              <div key={k} className="v5-lineage-selection-meta-row">
                <dt>{k}</dt>
                <dd>{String(v)}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {antecedents.length > 0 && (
        <section className="v5-lineage-selection-section" aria-label="Antecedents">
          <span className="v5-eyebrow">Antecedents ({antecedents.length})</span>
          <ul className="v5-lineage-selection-list">
            {antecedents.slice(0, 8).map((n) => (
              <li key={n.id} className="v5-lineage-selection-list-item">{n.label || n.id}</li>
            ))}
            {antecedents.length > 8 && (
              <li className="v5-lineage-selection-list-more">+{antecedents.length - 8} more</li>
            )}
          </ul>
        </section>
      )}

      {descendants.length > 0 && (
        <section className="v5-lineage-selection-section" aria-label="Descendants">
          <span className="v5-eyebrow">Descendants ({descendants.length})</span>
          <ul className="v5-lineage-selection-list">
            {descendants.slice(0, 8).map((n) => (
              <li key={n.id} className="v5-lineage-selection-list-item">{n.label || n.id}</li>
            ))}
            {descendants.length > 8 && (
              <li className="v5-lineage-selection-list-more">+{descendants.length - 8} more</li>
            )}
          </ul>
        </section>
      )}
    </aside>
  );
}

export function ThreadV5LineageExplorer({
  projectId,
  branch,
  selectedNodeId = null,
  onNodeSelect,
  onFullProvenance,
  fullSurface = false,
  width,
  height,
}: ThreadV5LineageExplorerProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [clusterMode, setClusterMode] = useState<ClusterMode>("units");
  const [clusterLegend, setClusterLegend] = useState<ClusterInfo[]>([]);
  const [selection, setSelection] = useState<LineageSelection | null>(null);
  // FXC-8632: search state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchHits, setSearchHits] = useState<ReadonlySet<string>>(new Set());
  const [searchLoading, setSearchLoading] = useState(false);
  // FXC-8632: onReady → zoomToFit
  const controlsRef = useRef<GraphControls | null>(null);

  const prevProjectBranchRef = useRef<string>("");

  // Reset all state on project/branch change so state does not leak.
  useEffect(() => {
    const key = `${projectId}:${branch}`;
    if (prevProjectBranchRef.current !== key) {
      prevProjectBranchRef.current = key;
      setCollapsed(false);
      setClusterMode("units");
      setClusterLegend([]);
      setSelection(null);
      setSearchQuery("");
      setDebouncedQuery("");
      setSearchHits(new Set());
    }
  }, [projectId, branch]);

  // FXC-8632: debounce the search input.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: rawDag, status, error } = useQuery({
    queryKey: ["lineage-explorer", projectId, branch],
    queryFn: () => fetchLineage(projectId, branch, { detail: "dag" }),
    staleTime: 60_000,
    enabled: Boolean(projectId),
  });

  const { graph, capped } = useMemo(() => {
    if (!rawDag) return { graph: null, capped: false };
    const full = fromProvenanceGraph(rawDag);
    return capGraph(full, DAG_NODE_RENDER_CAP);
  }, [rawDag]);

  // FXC-8632: Run semantic search when the debounced query changes.
  // Falls back to label substring match when the endpoint returns nothing.
  useEffect(() => {
    if (!debouncedQuery.trim() || !graph) {
      setSearchHits(new Set());
      setSearchLoading(false);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    semanticSearch(projectId, debouncedQuery, branch, 20)
      .then((response) => {
        if (cancelled) return;
        const hits = response.results.map((r) => r.node_id);
        if (hits.length > 0) {
          setSearchHits(new Set(hits));
        } else {
          // Endpoint returned nothing — use local label/substring fallback.
          setSearchHits(labelSearchFallback(graph, debouncedQuery));
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Fallback: local label match only.
        if (graph) setSearchHits(labelSearchFallback(graph, debouncedQuery));
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });
    return () => { cancelled = true; };
  }, [debouncedQuery, projectId, branch, graph]);

  const pivotId = useMemo(() => {
    if (!graph) return null;
    return pickPivotId(graph, selectedNodeId);
  }, [graph, selectedNodeId]);

  const handleSelect = useCallback((sel: LineageSelection) => {
    setSelection(sel);
    onNodeSelect?.(sel.node.id);
  }, [onNodeSelect]);

  const handleClusters = useCallback((clusters: ClusterInfo[], _mode: ClusterMode) => {
    setClusterLegend(clusters.slice(0, 6));
  }, []);

  const handleReady = useCallback((controls: GraphControls | null) => {
    controlsRef.current = controls;
  }, []);

  const handleZoomToFit = useCallback(() => {
    controlsRef.current?.zoomToFit(400);
  }, []);

  if (status === "pending") {
    return (
      <div className={`v5-lineage-explorer v5-lineage-explorer--loading${fullSurface ? " v5-lineage-explorer--full" : ""}`} aria-label="Loading lineage graph">
        <div className="v5-lineage-toolbar">
          <span className="v5-strip-title"><Icons.Dag size={13} />Lineage DAG</span>
        </div>
        <div className="v5-lineage-body">
          <div className="v5-lineage-spinner" role="status" aria-live="polite">Loading graph…</div>
        </div>
      </div>
    );
  }

  if (status === "error" || !graph || !pivotId) {
    const message = status === "error"
      ? (error instanceof Error ? error.message : "Unable to load graph")
      : !graph || graph.nodes.length === 0
        ? "No graph data for this project yet."
        : "Could not determine a graph pivot node.";
    return (
      <div className={`v5-lineage-explorer v5-lineage-explorer--empty${fullSurface ? " v5-lineage-explorer--full" : ""}`} aria-label="Lineage graph unavailable">
        <div className="v5-lineage-toolbar">
          <span className="v5-strip-title"><Icons.Dag size={13} />Lineage DAG</span>
        </div>
        <div className="v5-lineage-body v5-lineage-body--empty">
          <Icons.Dag size={28} />
          <p>{message}</p>
          {onFullProvenance && (
            <button className="v5-text-button" type="button" onClick={onFullProvenance}>
              Open provenance <Icons.ChevronRight size={13} />
            </button>
          )}
        </div>
      </div>
    );
  }

  const hasSelection = Boolean(selection);

  return (
    <div
      className={[
        "v5-lineage-explorer",
        collapsed ? "is-collapsed" : "",
        fullSurface ? "v5-lineage-explorer--full" : "",
        hasSelection && fullSurface ? "v5-lineage-explorer--has-selection" : "",
      ].filter(Boolean).join(" ")}
      data-testid="v5-lineage-explorer"
    >
      {/* Toolbar — FXC-8631: all chrome uses --v5-* tokens via CSS classes */}
      <div className="v5-lineage-toolbar">
        <span className="v5-strip-title">
          <Icons.Dag size={13} />
          Lineage DAG
          {/* FXC-8632: In compact (non-full) mode show focus chip; in full mode the
              selection panel shows the detail, so only show the chip when no panel. */}
          {!fullSurface && selection ? (
            <span className="v5-lineage-focus-chip">{selection.node.label || selection.node.id}</span>
          ) : null}
        </span>

        {/* FXC-8632: Semantic search box — visible in both modes, more prominent in full. */}
        <div className={`v5-lineage-search${fullSurface ? " v5-lineage-search--full" : ""}`} data-testid="v5-lineage-search">
          <label className="v5-sr-only" htmlFor={`lineage-search-${projectId}`}>Search nodes</label>
          <input
            id={`lineage-search-${projectId}`}
            className="v5-lineage-search-input"
            type="search"
            placeholder="Search nodes…"
            value={searchQuery}
            aria-label="Search lineage nodes"
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchLoading && <span className="v5-lineage-search-spinner" aria-hidden="true" />}
          {searchHits.size > 0 && !searchLoading && (
            <span className="v5-lineage-search-count" aria-live="polite">{searchHits.size}</span>
          )}
        </div>

        <div className="v5-lineage-toolbar-actions">
          {capped && (
            <span
              className="v5-chip"
              title={`Graph capped at ${DAG_NODE_RENDER_CAP.toLocaleString()} nodes for performance. Shuttle lazy-loading endpoint needed for full X-57 scale.`}
            >
              capped
            </span>
          )}
          {/* FXC-8632: zoomToFit button via onReady controls */}
          {fullSurface && (
            <button
              className="v5-icon-button"
              type="button"
              title="Fit graph to view"
              aria-label="Zoom to fit"
              onClick={handleZoomToFit}
            >
              <Icons.Refresh size={13} />
            </button>
          )}
          {/* FXC-8632: Cluster selector — productized as a V5 control */}
          <select
            className="v5-lineage-cluster-select"
            value={clusterMode}
            aria-label="Cluster mode"
            onChange={(e) => setClusterMode(e.target.value as ClusterMode)}
          >
            <option value="units">by unit</option>
            <option value="communities">by community</option>
            {/* cliques auto-downgrades to communities >80 visible nodes (LineageExplorer handles this) */}
            <option value="cliques">by clique</option>
            <option value="components">by component</option>
            <option value="none">no clusters</option>
          </select>
          <button
            className={`v5-icon-button${collapsed ? " is-active" : ""}`}
            type="button"
            title={collapsed ? "Expand clusters" : "Collapse clusters into super-nodes"}
            aria-pressed={collapsed}
            onClick={() => setCollapsed((prev) => !prev)}
          >
            <Icons.Layers size={13} />
          </button>
          {onFullProvenance && (
            <button
              className="v5-icon-button"
              type="button"
              title="Full provenance surface"
              onClick={onFullProvenance}
            >
              <Icons.ChevronRight size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Main layout: graph canvas + optional selection panel (full mode) */}
      <div className="v5-lineage-main">
        {/* Graph canvas */}
        <div className="v5-lineage-body" data-testid="v5-lineage-body">
          {/* FXC-8629 NOTE: colourForType/colourForPredicate/nodeColor are NOT passed here
              because <LineageExplorer> does not yet forward them to the renderer (only
              <ThreadGraph> supports them today). Once Warp lands FXC-8629
              (warp/FXC-8629-lineage-colour-overrides), wire:
                const v5Colour = (type: string) => getComputedStyle(document.documentElement)
                  .getPropertyValue(V5_TOKEN_FOR[type] ?? "--v5-primary").trim();
                <LineageExplorer colourForType={v5Colour} ... />
          */}
          <LineageExplorer
            graph={graph}
            focusId={pivotId}
            initialDepth={1}
            revealDepth={1}
            expandDirection="both"
            clusterBy={clusterMode}
            collapse={collapsed}
            sizeByDegree
            flowParticles={false}
            searchHits={searchHits.size > 0 ? searchHits : undefined}
            onSelect={handleSelect}
            onClusters={handleClusters}
            onReady={handleReady}
            width={width}
            height={height}
            className="v5-lineage-canvas"
          />
        </div>

        {/* FXC-8632: Selection detail panel — shown in full-surface mode when a node is selected */}
        {fullSurface && selection && (
          <SelectionPanel
            selection={selection}
            onDismiss={() => setSelection(null)}
          />
        )}
      </div>

      {/* FXC-8632: onClusters legend — rendered for both modes */}
      {clusterLegend.length > 0 && !collapsed && (
        <div className="v5-lineage-legend" data-testid="v5-lineage-legend" aria-label="Cluster legend">
          {clusterLegend.map((cluster) => (
            <span key={cluster.id} className="v5-lineage-legend-item">
              <span
                className="v5-lineage-legend-dot"
                style={{ background: cluster.color }}
                aria-hidden="true"
              />
              <span className="v5-lineage-legend-label">
                {cluster.dominantType} ({cluster.size})
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Scale note for data engineers */}
      <div className="v5-lineage-footer">
        <span className="v5-muted">
          {graph.nodes.length.toLocaleString()} nodes · click to expand · right-click to re-centre
          {searchHits.size > 0 ? ` · ${searchHits.size} search hits highlighted` : ""}
        </span>
      </div>
    </div>
  );
}
